import * as React from "react";

import { cn } from "@/lib/utils";

import { Skeleton } from "@/components/ui/Skeleton";
import { Callout } from "../Callout";

interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  fixed?: boolean;
  condensed?: boolean;
}

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, fixed, condensed, ...props }, ref) => (
    <div className="relative w-full overflow-auto">
      <table
        ref={ref}
        className={cn(
          "w-full border-collapse caption-bottom text-sm",
          fixed ? "table-fixed" : "table-auto",
          condensed
            ? "[&>thead>tr>th]:p-1.5 [&>tbody>tr>td]:p-1.5 [&>tfoot>tr>td]:p-1.5"
            : "[&>thead>tr>th]:p-3 [&>tbody>tr>td]:p-3 [&>tfoot>tr>td]:p-3",
          className,
        )}
        {...props}
      />
    </div>
  ),
);
Table.displayName = "Table";

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={className} {...props} />
));
TableHeader.displayName = "TableHeader";

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "border-t text-tertiary font-medium [&>tr]:last:border-b-0",
      className,
    )}
    {...props}
  />
));
TableFooter.displayName = "TableFooter";

// TODO: If we start wanting more decoration options,
// we should consider using variants instead of props
interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  strikethrough?: boolean;
  showSeparator?: boolean;
}
const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, strikethrough, showSeparator = true, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        "transition-colors",
        strikethrough && "text-tertiary",
        showSeparator ? "border-b border-primary" : "border-0",
        className,
      )}
      {...props}
    />
  ),
);
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-12 p-0 text-left align-middle font-medium text-tertiary [&:has([role=checkbox])]:pr-0",
      className,
    )}
    {...props}
  />
));
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "align-middle border-0 [&:has([role=checkbox])]:pr-0",
      className,
    )}
    {...props}
  />
));
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-tertiary", className)}
    {...props}
  />
));
TableCaption.displayName = "TableCaption";

interface TableBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  isLoading?: boolean;
  isError?: boolean;
  columnCount?: number;
}
const TableBody = React.forwardRef<HTMLTableSectionElement, TableBodyProps>(
  (
    {
      className,
      isLoading = false,
      isError = false,
      columnCount = 0,
      ...props
    },
    ref,
  ) => {
    if (isError) {
      return (
        <tbody className={className}>
          <TableRow>
            <TableCell colSpan={columnCount}>
              <Callout
                className="w-full"
                variant="danger"
                title="Something went wrong!"
                description="Please refresh and try again."
              />
            </TableCell>
          </TableRow>
        </tbody>
      );
    }

    if (isLoading) {
      return (
        <tbody className={className}>
          {Array.from({ length: 8 }, (_item, i) => (
            //  biome-ignore lint/suspicious/noArrayIndexKey: unneeded for loading skeleton
            <tr key={`loading-row-${i}`}>
              {Array.from({ length: columnCount }, (_, j) => (
                //  biome-ignore lint/suspicious/noArrayIndexKey: unneeded for loading skeleton
                <td key={`loading-cell-${j}`}>
                  <Skeleton className="h-6" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      );
    }

    return <tbody ref={ref} className={className} {...props} />;
  },
);
TableBody.displayName = "TableBody";

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
